/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React, { PropsWithChildren } from 'react';
import { Box, Button, Drawer, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type PopupStatus = 'expanded' | 'collapsed';

/** A point in CSS pixels from the top-left corner of the map container. */
export type ScreenPoint = { x: number; y: number };

/** A size in CSS pixels. */
export type ScreenSize = { width: number; height: number };

export type PopupPlacement = {
  /** Which side of the anchor the popup opens on. */
  side: 'below' | 'above';
  /**
   * Where the anchor sits along the popup's width: centred, or at the edge
   * nearest the map's edge so the popup hangs inward.
   */
  align: 'center' | 'start' | 'end';
  /**
   * The room on the roomier side of the anchor, whichever side the popup
   * opens on. A cap that followed the chosen side would make the measured
   * height depend on that choice and feed back into it.
   */
  maxHeight: number;
};

/**
 * Places a popup of the measured `size` against `anchor` so it stays inside
 * `bounds`: below the anchor when it fits there, otherwise on whichever side
 * has more room, and hanging inward when the anchor is near a side edge -- the
 * policy MapLibre's own popups follow. The result only chooses which edges the
 * popup is pinned to the anchor by, so a measurement that is a frame stale can
 * at most open it on the other side, never leave it detached from the anchor.
 */
export function placePopup(
  anchor: ScreenPoint,
  bounds: ScreenSize,
  size: ScreenSize
): PopupPlacement {
  const roomBelow = bounds.height - anchor.y;
  const roomAbove = anchor.y;
  const half = size.width / 2;
  return {
    side: size.height <= roomBelow || roomBelow >= roomAbove ? 'below' : 'above',
    align: anchor.x < half ? 'start' : anchor.x > bounds.width - half ? 'end' : 'center',
    maxHeight: Math.max(roomBelow, roomAbove),
  };
}

/** Height of the pointer triangle joining the popup to its anchor, in CSS pixels. */
const TIP_SIZE = 10;

const POPUP_MAX_WIDTH = 420;

/** Tallest the scrollable body gets before it scrolls, in CSS pixels. */
const BODY_MAX_HEIGHT = 400;

export const PopupContainer = (props: {
  children: React.ReactNode;
  onClose: () => void;
  /** The map point the popup stays attached to. */
  anchor: ScreenPoint;
  /** The map container the popup is kept inside of. */
  bounds: ScreenSize;
  expandable?: boolean;
}) => {
  const [popupStatus, setPopupStatus] = React.useState<PopupStatus>('collapsed');

  const { expandable = true } = props;

  const [lastExpandable, setLastExpandable] = React.useState(expandable);
  if (lastExpandable !== expandable) {
    setLastExpandable(expandable);
    if (!expandable) {
      setPopupStatus('collapsed');
    }
  }

  // The rendered size decides which side of the anchor the popup opens on.
  // Measured once laid out and again whenever the content changes size, as
  // when a structure's details arrive.
  const boxRef = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = React.useState<ScreenSize>({ width: 0, height: 0 });
  React.useLayoutEffect(() => {
    const box = boxRef.current;
    if (box == null) return;
    const measure = () =>
      setSize(previous =>
        previous.width === box.offsetWidth && previous.height === box.offsetHeight
          ? previous
          : { width: box.offsetWidth, height: box.offsetHeight }
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [popupStatus]);

  if (popupStatus === 'expanded') {
    return (
      <Drawer
        slotProps={{
          paper: {
            variant: 'outlined',
            elevation: 0,
            sx: {
              backgroundColor: 'rgb(245, 250, 252)',
              borderRight: 0,
              pointerEvents: 'auto',
              minWidth: 400,
            },
          },
        }}
        variant="persistent"
        anchor="right"
        open={popupStatus === 'expanded'}
        onClose={() => setPopupStatus('collapsed')}
      >
        <DetailsBox setPopupStatus={setPopupStatus} children={props.children} />
      </Drawer>
    );
  }

  const { side, align, maxHeight } = placePopup(props.anchor, props.bounds, size);

  return (
    // Pinned to the anchor by the CSS edges the placement chose. The map
    // container clips it at the map's edges, so it slides out of view with
    // its feature instead of spilling over the page chrome.
    <Box
      ref={boxRef}
      data-popup-side={side}
      data-popup-align={align}
      sx={{
        position: 'absolute',
        // The map's own level of the stacking order: over the deck canvas,
        // which precedes it in the DOM, and under every on-map control -- the
        // buttons, the stats panel, the layer controls -- as MapLibre stacks
        // its controls over its popups.
        zIndex: 0,
        display: 'flex',
        flexDirection: side === 'below' ? 'column' : 'column-reverse',
        // Pinned by one edge, an absolutely positioned box would otherwise
        // shrink to fit the room between it and the opposite map edge.
        width: 'max-content',
        maxWidth: `min(${POPUP_MAX_WIDTH}px, 100%)`,
        // In px: the sx shorthand reads a bare number of 1 or less as a fraction.
        maxHeight: `${maxHeight}px`,
        ...(side === 'below'
          ? { top: props.anchor.y }
          : { bottom: props.bounds.height - props.anchor.y }),
        ...(align === 'start'
          ? { left: props.anchor.x - TIP_SIZE }
          : align === 'end'
            ? { right: props.bounds.width - props.anchor.x - TIP_SIZE }
            : { left: props.anchor.x, transform: 'translateX(-50%)' }),
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 0,
          height: 0,
          flexShrink: 0,
          alignSelf: align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : 'center',
          borderLeft: `${TIP_SIZE}px solid transparent`,
          borderRight: `${TIP_SIZE}px solid transparent`,
          ...(side === 'below'
            ? { borderBottom: `${TIP_SIZE}px solid`, borderBottomColor: 'background.paper' }
            : { borderTop: `${TIP_SIZE}px solid`, borderTopColor: 'background.paper' }),
        }}
      />
      <Paper elevation={4} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            px: 1,
            pt: 0.5,
          }}
        >
          {expandable && (
            <Button size="small" onClick={() => setPopupStatus('expanded')}>
              Expand
            </Button>
          )}
          <IconButton size="small" aria-label="Close" onClick={props.onClose} sx={{ ml: 'auto' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            maxHeight: BODY_MAX_HEIGHT,
            minHeight: 0,
            overflow: 'auto',
            // Reserved up front, so a scrollbar that appears once a section is
            // expanded cannot take width the details' fixed minimum width
            // cannot give up, which would show as a horizontal scrollbar.
            scrollbarGutter: 'stable',
          }}
        >
          {props.children}
        </Box>
      </Paper>
    </Box>
  );
};

const DetailsBox = (
  props: PropsWithChildren<{
    setPopupStatus: React.Dispatch<React.SetStateAction<PopupStatus>>;
  }>
) => {
  return (
    <Box sx={{ pt: 18 }}>
      <div>
        <Button
          onClick={() => {
            props.setPopupStatus('collapsed');
          }}
        >
          Close
        </Button>
      </div>
      {props.children}
    </Box>
  );
};
